import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Eye, EyeOff, Loader2, ChevronDown, ChevronUp, MapPin, CheckCircle, UserPlus, Gift, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSiteContent } from '../hooks/useSiteContent';

import { BACKEND_URL } from '../config/api';

const TERMS_AND_CONDITIONS = `OnsiteHand is a UK-based connection platform that introduces independent tradespeople to employers and does not employ, supply, manage, supervise, or pay tradespeople, nor does it act as an employment or recruitment agency. All tradespeople using the platform are independent contractors, and all work arrangements, payments, agreements, and communications take place directly between the employer and the tradesperson, with OnsiteHand not being a party to any agreement. OnsiteHand does not guarantee the availability, suitability, quality, conduct, or performance of any user, and is not responsible for disputes, payments, delays, losses, injuries, or damages arising from interactions between users. Ratings and reviews are user-generated and reflect individual experiences only, and while qualifications or certificates may be displayed, employers remain responsible for verifying suitability, compliance, insurance, and right-to-work. OnsiteHand reserves the right to suspend or remove any user at its discretion, and all use of the platform is governed by the laws of England and Wales.`;

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { get } = useSiteContent();
  const [showPassword, setShowPassword] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [postcodeStatus, setPostcodeStatus] = useState(null); // null, 'checking', 'available', 'priority', 'unavailable'
  const [activeRegions, setActiveRegions] = useState([]);
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '');
  const [referralValid, setReferralValid] = useState(null); // null, true, false
  const [referrerName, setReferrerName] = useState('');
  const [workerType, setWorkerType] = useState('');
  const [directWorkDeclaration, setDirectWorkDeclaration] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    // Spec §Make role selection explicit — no default. `null` means the
    // user has not yet picked a role and the form must remain gated.
    user_type: searchParams.get('type') === 'tradesperson' || searchParams.get('type') === 'employer'
      ? searchParams.get('type')
      : null,
    postcode: searchParams.get('postcode') || '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const inviteToken = searchParams.get('invite');

  useEffect(() => {
    fetchActiveRegions();
    // If ref param exists, validate it
    if (searchParams.get('ref')) {
      validateReferralCode(searchParams.get('ref'));
    }
  }, []);

  const fetchActiveRegions = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/regions/active`);
      setActiveRegions(response.data.regions || []);
    } catch (error) {
      console.error('Failed to fetch active regions');
    }
  };

  const validateReferralCode = async (code) => {
    if (!code || code.length < 5) {
      setReferralValid(null);
      setReferrerName('');
      return;
    }
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/referral/validate/${code.toUpperCase()}`);
      if (response.data.valid) {
        setReferralValid(true);
        setReferrerName(response.data.referrer_name);
        toast.success(`Referral code from ${response.data.referrer_name} applied!`);
      } else {
        setReferralValid(false);
        setReferrerName('');
      }
    } catch (error) {
      setReferralValid(false);
      setReferrerName('');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Reset postcode status when postcode changes
    if (name === 'postcode') {
      setPostcodeStatus(null);
    }
  };

  const handleReferralCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setReferralCode(code);
    setReferralValid(null);
    setReferrerName('');
  };

  const handleReferralCodeBlur = () => {
    if (referralCode) {
      validateReferralCode(referralCode);
    }
  };

  const checkPostcode = async () => {
    if (!formData.postcode || formData.postcode.length < 2) {
      toast.error('Please enter a valid UK postcode');
      return;
    }

    setPostcodeStatus('checking');
    try {
      const response = await axios.post(`${BACKEND_URL}/api/expansion/check-area`, {
        postcode: formData.postcode
      });
      
      if (response.data.status === 'live') {
        setPostcodeStatus('available');
        toast.success(`Great! We're active in ${response.data.area_name}`);
      } else if (response.data.status === 'priority') {
        setPostcodeStatus('priority');
        toast.info(response.data.message);
      } else {
        setPostcodeStatus('unavailable');
      }
    } catch (error) {
      setPostcodeStatus(null);
      toast.error('Failed to check postcode');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      toast.error('Please agree to the Terms and Conditions to continue');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    // Check if postcode is required (only if regions exist and no invite token)
    if (activeRegions.length > 0 && !inviteToken && !formData.postcode) {
      toast.error('Please enter your postcode');
      return;
    }
    
    // Tradesperson-specific validation
    if (formData.user_type === 'tradesperson') {
      if (!workerType) {
        toast.error('Please select what best describes you');
        return;
      }
      if (!directWorkDeclaration) {
        toast.error('Please confirm that you carry out work directly');
        return;
      }
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/register`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        user_type: formData.user_type,
        postcode: formData.postcode,
        phone: formData.phone,
        terms_accepted: true,
        terms_accepted_at: new Date().toISOString(),
        invite_token: inviteToken,
        referral_code: referralCode || null,
        worker_type: formData.user_type === 'tradesperson' ? workerType : undefined,
        direct_work_declaration: formData.user_type === 'tradesperson' ? directWorkDeclaration : undefined,
      });
      
      // Check if user is waitlisted
      if (response.data.region_status === 'waitlisted') {
        toast.success('Registration successful! You\'re on the waitlist.');
        setTimeout(() => navigate('/waitlist-status'), 2000);
      } else if (response.data.approval_status === 'pending') {
        toast.success('Registration successful! Your account is pending admin approval. We\'ll notify you once approved.');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        toast.success('Registration successful! Please check your email to verify your account.');
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        
        <div className="bg-white rounded-xl shadow-lg p-8 border border-slate-200">
          <div className="flex items-center justify-center gap-2 mb-8">
            <Building2 className="h-8 w-8 text-orange-500" data-testid="register-logo" />
            <h1 className="text-2xl font-bold text-slate-900">OnsiteHand</h1>
          </div>
          
          {inviteToken && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">You've been invited!</span>
              </div>
              <p className="text-green-600 text-sm mt-1">Complete your signup to get started.</p>
            </div>
          )}
          
          {/* Referral Code Banner */}
          {referralValid && referrerName && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6" data-testid="referral-banner">
              <div className="flex items-center gap-2 text-orange-700">
                <Gift className="h-5 w-5" />
                <span className="font-medium">Referred by {referrerName}!</span>
              </div>
              <p className="text-orange-600 text-sm mt-1">Your referral has been applied.</p>
            </div>
          )}
          
          <h2 className="text-2xl font-bold text-slate-900 mb-2 text-center" data-testid="register-title">Create Account</h2>
          <p className="text-slate-600 text-center mb-6">Takes less than a minute</p>
          
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="As you'd like to appear on the platform"
                className="w-full"
                data-testid="register-name-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="you@example.com"
                className="w-full"
                data-testid="register-email-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                  minLength={6}
                  className="w-full pr-10"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Confirm Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                  minLength={6}
                  className={`w-full pr-10 ${formData.confirmPassword && formData.password !== formData.confirmPassword ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                  data-testid="register-confirm-password-input"
                />
              </div>
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="text-xs text-red-500 mt-1" data-testid="password-mismatch-error">Passwords do not match</p>
              )}
            </div>
            
            <div data-testid="register-role-picker">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                I want to... <span className="text-rose-500">*</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData((f) => ({ ...f, user_type: 'tradesperson' }))}
                  className={`text-left rounded-xl border-2 p-4 transition ${
                    formData.user_type === 'tradesperson'
                      ? 'border-orange-500 bg-orange-50 shadow-sm'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                  data-testid="role-picker-trade"
                  aria-pressed={formData.user_type === 'tradesperson'}
                >
                  <div className="text-sm font-semibold text-slate-900">I want work</div>
                  <div className="text-xs text-slate-500 mt-1">
                    Trades, labourers, subcontractors, small teams / gangs, limited-company contractors.
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData((f) => ({ ...f, user_type: 'employer' }))}
                  className={`text-left rounded-xl border-2 p-4 transition ${
                    formData.user_type === 'employer'
                      ? 'border-orange-500 bg-orange-50 shadow-sm'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                  data-testid="role-picker-employer"
                  aria-pressed={formData.user_type === 'employer'}
                >
                  <div className="text-sm font-semibold text-slate-900">I need workers</div>
                  <div className="text-xs text-slate-500 mt-1">
                    Builders, main contractors, site managers, developers, homeowners, direct hirers.
                  </div>
                </button>
              </div>
              {!formData.user_type && (
                <p className="text-xs text-amber-600 mt-2" data-testid="role-required-hint">
                  Please choose one to continue.
                </p>
              )}
            </div>
            
            {/* Worker Type - Only show for tradespeople */}
            {formData.user_type === 'tradesperson' && (
              <div className="space-y-3" data-testid="worker-type-section">
                <label className="block text-sm font-medium text-slate-700">What best describes you?</label>
                <div className="space-y-2">
                  {[
                    { value: 'individual_tradesperson', label: 'Individual tradesperson' },
                    { value: 'self_employed_subcontractor', label: 'Self-employed subcontractor' },
                    { value: 'small_trade_team_gang', label: 'Small trade team/gang' },
                    { value: 'limited_company_trade_contractor', label: 'Limited company trade contractor' },
                  ].map(opt => (
                    <label key={opt.value}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        workerType === opt.value
                          ? 'border-orange-400 bg-orange-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`worker-type-${opt.value}`}
                    >
                      <input
                        type="radio"
                        name="worker_type"
                        value={opt.value}
                        checked={workerType === opt.value}
                        onChange={(e) => setWorkerType(e.target.value)}
                        className="text-orange-500 focus:ring-orange-400"
                      />
                      <span className="text-sm text-slate-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-500">
                  OnSiteHand is for tradespeople, subcontractors and trade teams who carry out the work directly.
                </p>
              </div>
            )}
            
            {/* Postcode Field - Only show if regions exist */}
            {(activeRegions.length > 0 || inviteToken) && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  <MapPin className="inline h-4 w-4 mr-1" />
                  Postcode
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="postcode"
                    value={formData.postcode}
                    onChange={handleChange}
                    placeholder="e.g., SW1A 1AA"
                    className="flex-1"
                    data-testid="register-postcode-input"
                  />
                  {!inviteToken && (
                    <button
                      type="button"
                      onClick={checkPostcode}
                      disabled={postcodeStatus === 'checking'}
                      className="btn-secondary text-sm px-3"
                      data-testid="check-postcode-btn"
                    >
                      {postcodeStatus === 'checking' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Check'
                      )}
                    </button>
                  )}
                </div>
                
                {/* Postcode Status Messages */}
                {postcodeStatus === 'available' && (
                  <div className="mt-2 text-sm text-green-600 flex items-center gap-1">
                    <CheckCircle className="h-4 w-4" />
                    We're active in your area!
                  </div>
                )}
                
                {postcodeStatus === 'unavailable' && (
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-700 font-medium">
                      {get('register.postcode_unavailable_title', "We're not active in your area yet")}
                    </p>
                    <p className="text-xs text-amber-600 mt-1">
                      {get('register.postcode_unavailable_desc', "You can still register and we'll add you to the waitlist. Every signup helps us decide where to launch next.")}
                    </p>
                  </div>
                )}
                
                {postcodeStatus === 'priority' && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700 font-medium">
                      Strong interest in your area
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      We're seeing demand near you. Register now and you'll be first to know when we go live.
                    </p>
                  </div>
                )}
              </div>
            )}
            
            {/* Phone Field (Optional) */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Phone Number <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="07123 456789"
                className="w-full"
                data-testid="register-phone-input"
              />
            </div>
            
            {/* Referral Code Field */}
            {!searchParams.get('ref') && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  <UserPlus className="inline h-4 w-4 mr-1" />
                  Referral Code <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={referralCode}
                  onChange={handleReferralCodeChange}
                  onBlur={handleReferralCodeBlur}
                  placeholder="e.g., TRADE-AB12CD"
                  className={`w-full ${
                    referralValid === true ? 'border-green-500 focus:ring-green-500' : 
                    referralValid === false ? 'border-red-500 focus:ring-red-500' : ''
                  }`}
                  data-testid="register-referral-input"
                />
                {referralValid === true && (
                  <p className="mt-1 text-sm text-green-600 flex items-center gap-1">
                    <CheckCircle className="h-4 w-4" />
                    Valid code from {referrerName}
                  </p>
                )}
                {referralValid === false && (
                  <p className="mt-1 text-sm text-red-600">Invalid referral code</p>
                )}
              </div>
            )}
            
            {/* Direct Work Declaration - Only for tradespeople */}
            {formData.user_type === 'tradesperson' && (
              <div className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="direct-work-declaration">
                <input
                  type="checkbox"
                  id="direct-work-declaration"
                  checked={directWorkDeclaration}
                  onChange={(e) => setDirectWorkDeclaration(e.target.checked)}
                  className="mt-0.5 rounded border-slate-300 text-orange-500 focus:ring-orange-400"
                  data-testid="direct-work-checkbox"
                />
                <label htmlFor="direct-work-declaration" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
                  I confirm that I carry out the work myself or as part of my own direct trade team/company. I am not signing up as a recruitment agency, labour agency, lead seller, or third-party labour supplier.
                </label>
              </div>
            )}
            
            {/* Terms and Conditions */}
            <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="terms-section">
              <button
                type="button"
                onClick={() => setShowTerms(!showTerms)}
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                data-testid="toggle-terms-btn"
              >
                <span className="text-sm font-medium text-slate-700">Terms and Conditions</span>
                {showTerms ? (
                  <ChevronUp className="h-4 w-4 text-slate-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-slate-500" />
                )}
              </button>
              {showTerms && (
                <div className="px-4 py-3 bg-white border-t border-slate-200 max-h-48 overflow-y-auto">
                  <p className="text-xs text-slate-600 leading-relaxed" data-testid="terms-text">
                    {TERMS_AND_CONDITIONS}
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="agree-terms"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-1 h-4 w-4 text-orange-500 border-slate-300 rounded focus:ring-orange-500"
                data-testid="agree-terms-checkbox"
              />
              <label htmlFor="agree-terms" className="text-sm text-slate-600">
                I agree to the{' '}
                <button
                  type="button"
                  onClick={() => window.open('/terms', '_blank')}
                  className="text-orange-500 hover:text-orange-600 font-medium"
                >
                  Terms of Service
                </button>
                {' '}and{' '}
                <button
                  type="button"
                  onClick={() => window.open('/privacy', '_blank')}
                  className="text-orange-500 hover:text-orange-600 font-medium"
                >
                  Privacy Policy
                </button>
                . I understand that OnsiteHand is a connection platform and is not a party to any agreements between users.
              </label>
            </div>
            
            <button
              type="submit"
              disabled={loading || !formData.user_type}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="register-submit-btn"
              title={!formData.user_type ? 'Choose a role to continue' : undefined}
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>
          
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-slate-500">Or continue with</span>
              </div>
            </div>
            
            <button
              onClick={handleGoogleSignup}
              className="btn-secondary w-full mt-4 flex items-center justify-center gap-2"
              data-testid="google-signup-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign up with Google
            </button>
          </div>
          
          <p className="text-center text-sm text-slate-600 mt-6">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-orange-500 font-medium hover:text-orange-600"
              data-testid="login-link"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
